import PropTypes from "prop-types"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { useEffect } from "react"
import DeleteTimeOff from "../../../DeleteTimeOff"
import { useDispatch, useSelector } from "react-redux"
import { getCalendarTimeOff } from "store/calendar"
import { useTimeOffDeletion } from "../../hooks/useTimeOffDeletion"
import { TIME_OFF_BE_KEYS, getTimeOffColumns } from "pagesV2/pro/Calendar/constants"
import TimeOffWeekdays from "./TimeOffWeekdays"
import TimeOffRange from "./TimeOffRange"
import TimeOffSingleDay from "./TimeOffSingleDay"
import styles from "./index.module.css"
import { Form, Skeleton } from "antd"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"

dayjs.extend(customParseFormat)

const TimeOffEntries = ({ type, form, setEditingTimeOffItem }) => {
  const { t } = useTranslation()
  const { timeOff } = useSelector((state) => state.calendar)
  const rawProfileId = Form.useWatch("crew_member_id", form)
  const profileId = rawProfileId === undefined ? undefined : (rawProfileId || null)
  const dispatch = useDispatch()
  const { isDeleteModalOpen, handleDeleteTimeOff, handleCancelDeleteTimeOff, handleYesDeleteTimeOff } = useTimeOffDeletion({
    setEditingTimeOffItem,
    form,
    timeOffType: type,
    profileId
  })

  const handleEditTimeOff = (timeOffItem) => {
    const currentCrewMemberId = form.getFieldValue("crew_member_id")
    form.resetFields()
    setEditingTimeOffItem(timeOffItem)

    const baseFields = {
      crew_member_id: currentCrewMemberId,
      notes: timeOffItem?.notes ?? timeOffItem?.note ?? "",
      recurringYearly: timeOffItem?.recurring_yearly ?? false
    }

    const toDayjsTime = (val) => {
      if (!val) return null
      const d = dayjs(val, ["hh:mm A", "hh:mm a"], true)
      return d.isValid() ? d : null
    }
    const toDayjsDate = (val) => {
      if (!val) return null
      const d = dayjs(val, ["YYYY-MM-DD", "MM/DD/YYYY"], true)
      return d.isValid() ? d : null
    }

    switch (type) {
      case TIME_OFF_BE_KEYS.weekDays:
        {
          const relatedEntries = (timeOff?.data || []).filter(
            (entry) => entry.season_start === timeOffItem?.season_start && entry.season_end === timeOffItem?.season_end
          )
          const selectedDays = relatedEntries.map((entry) => entry.unavailable_weekday).filter(Boolean)
          const dayFields = {}
          relatedEntries.forEach((entry) => {
            const day = entry.unavailable_weekday
            if (day) {
              dayFields[`startTime_${day}`] = toDayjsTime(entry.start_time)
              dayFields[`endTime_${day}`] = toDayjsTime(entry.end_time)
              dayFields[`fullDay_${day}`] = !!entry.all_day
            }
          })

          form.setFieldsValue({
            ...baseFields,
            timeOffType: TIME_OFF_BE_KEYS.weekDays,
            selectedDays,
            ...dayFields,
            weeksStartDate: toDayjsDate(timeOffItem?.season_start),
            weeksEndDate: toDayjsDate(timeOffItem?.season_end)
          })
        }
        break

      case TIME_OFF_BE_KEYS.dateRange:
        form.setFieldsValue({
          ...baseFields,
          timeOffType: TIME_OFF_BE_KEYS.dateRange,
          dateRangeFromDate: toDayjsDate(timeOffItem?.season_start),
          dateRangeToDate: toDayjsDate(timeOffItem?.season_end),
          dateRangeFromTime: toDayjsTime(timeOffItem?.start_time),
          dateRangeToTime: toDayjsTime(timeOffItem?.end_time)
        })
        break

      case TIME_OFF_BE_KEYS.singleDay:
        form.setFieldsValue({
          ...baseFields,
          timeOffType: TIME_OFF_BE_KEYS.singleDay,
          singleDayDate: toDayjsDate(timeOffItem?.season_start),
          singleDayStartTime: toDayjsTime(timeOffItem?.start_time),
          singleDayEndTime: toDayjsTime(timeOffItem?.end_time),
          singleDayFullDay: !!timeOffItem?.all_day
        })
        break
      default:
        break
    }
  }

  useEffect(() => {
    if (profileId === undefined) return
    dispatch(getCalendarTimeOff({ ui_type: type }, profileId))
  }, [type, profileId, dispatch])

  const getDropdownItems = (timeOffItem) => [
    {
      key: "editTimeOff",
      label: t("calendar.schedule_time_off.edit_time_off"),
      onClick: () => handleEditTimeOff(timeOffItem)
    },
    {
      key: "deleteTimeOff",
      label: t("calendar.schedule_time_off.delete_time_off"),
      onClick: () => handleDeleteTimeOff(timeOffItem?.id)
    }
  ]

  const columns = getTimeOffColumns(type)

  const renderTimeOffDetailsBasedOnType = (type) => {
    switch (type) {
      case TIME_OFF_BE_KEYS.weekDays:
        return <TimeOffWeekdays timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      case TIME_OFF_BE_KEYS.dateRange:
        return <TimeOffRange timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      case TIME_OFF_BE_KEYS.singleDay:
        return <TimeOffSingleDay timeOff={timeOff?.data || []} getDropdownItems={getDropdownItems} />
      default:
        return null
    }
  }

  return (
    <div className={styles.timeOffEntriesContainer}>
      <table className={`${styles.timeOffTable} w-full`}>
        {timeOff.data.length > 0 && (
          <thead>
            {columns?.map((column, index) => {
              const isLast = index === columns.length - 1
              return (
                <th key={column || index} className={isLast ? styles.actionColumn : ""}>
                  <Text fw={500}>{column}</Text>
                </th>
              )
            })}
          </thead>
        )}
        <Skeleton loading={timeOff?.loading} active paragraph={{ rows: 1 }} className="mt-l">
          {renderTimeOffDetailsBasedOnType(type)}
        </Skeleton>
        {isDeleteModalOpen && (
          <DeleteTimeOff open={isDeleteModalOpen} onCancel={handleCancelDeleteTimeOff} onYes={handleYesDeleteTimeOff} />
        )}
      </table>
    </div>
  )
}

TimeOffEntries.propTypes = {
  type: PropTypes.string,
  form: PropTypes.object.isRequired,
  setEditingTimeOffItem: PropTypes.func.isRequired
}

export default TimeOffEntries
